/**
 * Life4Earth V2 — Client-side notification store.
 *
 * Features:
 *   - Newest-first ordering
 *   - Active-conversation suppression (no notifications for the chat you're looking at)
 *   - Grouped view: collapses multiple unread chat_message notifications from the
 *     same sender into a single "{name} sent you X messages" entry
 */
import type { AppNotification, NotificationType } from './types';
import { markNotificationRead, markAllNotificationsRead } from '../api/chat';

/* ------------------------------------------------------------------ */
/*  Store state                                                        */
/* ------------------------------------------------------------------ */

let notifications: AppNotification[] = [];
let listeners: Array<() => void> = [];

const MAX_NOTIFICATIONS = 100;

/**
 * The conversation the user currently has open (ChatWindow or MessagesPage).
 * Notifications for this conversation are suppressed.
 */
let activeConversationId: string | null = null;

/* ------------------------------------------------------------------ */
/*  Active conversation (suppress in-chat notifications)               */
/* ------------------------------------------------------------------ */

/** Set the conversation the user is currently viewing. Pass null to clear. */
export function setActiveConversation(conversationId: string | null): void {
  activeConversationId = conversationId;
}

/** Get the currently active conversation ID (used by bridge to suppress). */
export function getActiveConversation(): string | null {
  return activeConversationId;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/** Get all notifications, newest first. */
export function getNotifications(): AppNotification[] {
  return notifications;
}

/**
 * Get a grouped view of notifications for display.
 *
 * Unread chat_message notifications from the same sender are collapsed into
 * a single entry: "{name} sent you X messages". Read notifications and
 * non-chat notifications pass through unchanged.
 */
export function getGroupedNotifications(): AppNotification[] {
  // Bucket unread chat_message notifications by sender (actorName + href combo)
  const senderBuckets = new Map<string, AppNotification[]>();
  const result: Array<AppNotification | { _groupKey: string }> = [];
  const insertedGroupKeys = new Set<string>();

  for (const n of notifications) {
    if (n.type === 'chat_message' && !n.isRead && n.actorName && n.href) {
      const groupKey = `${n.actorName}::${n.href}`;
      if (!senderBuckets.has(groupKey)) {
        senderBuckets.set(groupKey, []);
      }
      senderBuckets.get(groupKey)!.push(n);
      // Insert a placeholder at the position of the FIRST (newest) occurrence
      if (!insertedGroupKeys.has(groupKey)) {
        insertedGroupKeys.add(groupKey);
        result.push({ _groupKey: groupKey } as any);
      }
    } else {
      result.push(n);
    }
  }

  // Replace placeholders with grouped or single notifications
  return result.map((item) => {
    const gk = (item as any)._groupKey;
    if (gk) {
      const bucket = senderBuckets.get(gk)!;
      if (bucket.length === 1) return bucket[0]; // Single message — no grouping needed
      // Create a synthetic grouped notification using the most recent message's timestamp
      const mostRecent = bucket.reduce((latest, current) =>
        new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
      );
      return {
        ...mostRecent,
        id: `group:${gk}`,
        body: undefined, // Remove individual message preview
        groupCount: bucket.length,
        groupIds: bucket.map((n) => n.id),
      } as AppNotification;
    }
    return item as AppNotification;
  });
}

/** Get unread count. */
export function getUnreadCount(): number {
  return notifications.filter((n) => !n.isRead).length;
}

/** Add a notification (deduplicates by dedupeKey, suppresses active-chat). */
export function addNotification(notif: AppNotification): void {
  // Suppress chat_message notifications for the conversation the user is viewing
  if (notif.type === 'chat_message' && activeConversationId && notif.href) {
    // href format: /messages/{conversationId}
    if (notif.href.includes(activeConversationId)) {
      // Mark it as read on the server so it doesn't resurface on next poll
      if (!notif.isRead) {
        markNotificationRead(notif.id).catch(() => { /* silent */ });
      }
      return; // User is already reading this conversation
    }
  }

  if (notif.dedupeKey) {
    const exists = notifications.some((n) => n.dedupeKey === notif.dedupeKey);
    if (exists) return; // Already present
  }
  notifications = [notif, ...notifications].slice(0, MAX_NOTIFICATIONS);
  emit();
}

/**
 * Bulk-load notifications from the server, preserving order.
 * Unlike addNotification (which prepends one at a time and would reverse
 * a newest-first server response), this merges the entire batch at once
 * while respecting deduplication and active-conversation suppression.
 */
export function bulkLoadNotifications(incoming: AppNotification[]): void {
  const existingKeys = new Set(
    notifications.filter((n) => n.dedupeKey).map((n) => n.dedupeKey)
  );

  const toAdd: AppNotification[] = [];
  for (const notif of incoming) {
    // Suppress active-conversation notifications
    if (notif.type === 'chat_message' && activeConversationId && notif.href) {
      if (notif.href.includes(activeConversationId)) {
        if (!notif.isRead) {
          markNotificationRead(notif.id).catch(() => { /* silent */ });
        }
        continue;
      }
    }
    // Deduplicate
    if (notif.dedupeKey && existingKeys.has(notif.dedupeKey)) continue;
    if (notif.dedupeKey) existingKeys.add(notif.dedupeKey);
    toAdd.push(notif);
  }

  if (toAdd.length === 0) return;

  // Merge: new items go in front (they're already newest-first from server),
  // then existing items that weren't duplicates.
  notifications = [...toAdd, ...notifications].slice(0, MAX_NOTIFICATIONS);
  emit();
}

/** Mark a single notification (or all in a group) as read (local + server). */
export function markRead(id: string, groupIds?: string[]): void {
  const idsToMark = groupIds ?? [id];
  const idSet = new Set(idsToMark);
  notifications = notifications.map((n) =>
    idSet.has(n.id) ? { ...n, isRead: true } : n
  );
  emit();
  // Persist each to server (fire-and-forget)
  idsToMark.forEach((nid) => {
    if (!nid.startsWith('group:')) {
      markNotificationRead(nid).catch(() => { /* silent */ });
    }
  });
  // Trigger messages badge refresh
  dispatchBadgeChanged();
}

/** Mark all notifications as read (local + server). */
export function markAllRead(): void {
  notifications = notifications.map((n) => ({ ...n, isRead: true }));
  emit();
  // Persist to server (fire-and-forget)
  markAllNotificationsRead().catch(() => { /* silent */ });
  // Trigger messages badge refresh
  dispatchBadgeChanged();
}

/** Remove a notification. */
export function removeNotification(id: string): void {
  notifications = notifications.filter((n) => n.id !== id);
  emit();
}

/** Clear all notifications of a specific type. */
export function clearByType(type: NotificationType): void {
  notifications = notifications.filter((n) => n.type !== type);
  emit();
}

/**
 * Mark all chat_message notifications for a conversation as read.
 * Called when the user opens a conversation in ChatWindow.
 * This ensures the notification bell's count resets and doesn't show
 * stale notifications for conversations the user is actively viewing.
 */
export function markConversationNotificationsAsRead(conversationId: string): void {
  const conversationUrl = `/messages/${conversationId}`;
  const notificationsToMark: string[] = [];

  notifications = notifications.map((n) => {
    if (n.type === 'chat_message' && !n.isRead && n.href === conversationUrl) {
      notificationsToMark.push(n.id);
      return { ...n, isRead: true };
    }
    return n;
  });

  if (notificationsToMark.length > 0) {
    emit();
    // Persist to server (fire-and-forget)
    notificationsToMark.forEach((id) => {
      markNotificationRead(id).catch(() => { /* silent */ });
    });
    // Trigger messages badge refresh
    dispatchBadgeChanged();
  }
}

/* ------------------------------------------------------------------ */
/*  Subscription (React integration via useSyncExternalStore)          */
/* ------------------------------------------------------------------ */

/** Subscribe to store changes. Returns an unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/** Snapshot for React useSyncExternalStore. */
export function getSnapshot(): AppNotification[] {
  return notifications;
}

/* ------------------------------------------------------------------ */
/*  Internal                                                           */
/* ------------------------------------------------------------------ */

function emit(): void {
  listeners.forEach((fn) => fn());
}

/** Dispatch a window event to tell App.tsx to refresh the messages badge. */
function dispatchBadgeChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent('l4e_badges_changed'));
  } catch { /* SSR guard */ }
}
